import type { TransformNode } from "@babylonjs/core";
import { PISTOL, type Equipment, type LadderSpec } from "@tiny-seas/shared";

export type CharacterAction = "idle" | "dig" | "wheel" | "cannon" | "capstan" | "anchorKick" | "chest" | "swim" | "repair" | "fill" | "throw" | "recover" | "ladder" | "launch";

/**
 * One reusable presentation input for any character rig. Reuse the object each
 * frame. Attack phase is elapsed seconds, or -1 when idle.
 */
export interface CharacterPose {
  visible: boolean;
  equipment: Equipment;
  /** The held bucket (equipment "bucket") carries water. */
  bucketFull: boolean;
  action: CharacterAction;
  /** Authoritative station ID, including cannon:<index> and islandCannon:<id>. */
  station: string;
  speed: number;
  attackPhase: number;
  attackSerial: number;
  blocking: boolean;
  actionProgress: number;
  /** A repair worked standing (helm, rigging, canvas) rather than kneeling at the hull or a gun. */
  repairStanding: boolean;
  climbProgress: number;
  reducedMotion: boolean;
  attackStep: number;
  attackDuration: number;
  staggerRemaining: number;
  crouched: boolean;
  sprinting: boolean;
  slideProgress: number;
  slideYaw: number;
  scopeBlend: number;
  pistolRounds: number;
  pistolReserve: number;
  pistolAimBlend: number;
  pistolReloadProgress: number;
  pistolRecoil: number;
  pitch: number;
  recoil: number;
  parryRaise: number;
  cannonRecoil: number;
  /** Seconds since this character was struck by a cutlass, else -1. Presentation only. */
  hitFlinch: number;
  /** Combo step (0…2) of the cut that struck it. */
  hitStep: number;
  /** Full finger articulation; distant characters skip it. */
  detail: boolean;
  /** Body-frame support contact. */
  grounded: boolean;
  /** Body-frame vertical speed, m/s. */
  verticalSpeed: number;
  /** Rendered sea height a surface swimmer rides, already faded with depth; 0 off the surface. */
  waterHeave: number;
  /** The actual animated wheel, not a duplicate camera-space wheel. */
  wheel: TransformNode | null;
  /** Collision-aligned frame root (interpolated ship or tower ladder frame) for the attached ladder. */
  climbRoot: TransformNode | null;
  /** The attached ladder's geometry; hull ladders by default. */
  climbSpec?: LadderSpec;
  /** The actual animated barrel the gunner mans, not a camera-space stand-in. */
  cannon: TransformNode | null;
  /** The turning capstan head whose bar a `capstan:<bar>` pusher grips. */
  capstan: TransformNode | null;
}

export function createCharacterPose(): CharacterPose {
  return {
    visible: false, equipment: "shovel", bucketFull: false, action: "idle", station: "", speed: 0,
    attackPhase: -1, attackSerial: 0, blocking: false, actionProgress: 0, repairStanding: false, climbProgress: 0,
    reducedMotion: false, wheel: null, climbRoot: null, cannon: null, capstan: null,
    attackStep: 0, attackDuration: 0, staggerRemaining: 0, crouched: false, sprinting: false,
    slideProgress: -1, slideYaw: 0, scopeBlend: 0, pitch: 0,
    recoil: 0, parryRaise: 0, cannonRecoil: 0, hitFlinch: -1, hitStep: 0,
    pistolRounds: PISTOL.capacity, pistolReserve: 0, pistolAimBlend: 0, pistolReloadProgress: -1, pistolRecoil: 0,
    detail: true, grounded: true, verticalSpeed: 0, waterHeave: 0,
  };
}

export interface ActionFacts {
  launching: boolean;
  station: string;
  carrying: boolean;
  swimming: boolean;
  /** Server work action: "repair", "fill", "throw", "anchorKick", or anything else. */
  work: string;
  recovering: boolean;
  digging: boolean;
}

/** Single action priority shared by the local player, remotes and the labs. */
export function resolveCharacterAction(f: ActionFacts): CharacterAction {
  if (f.launching) return "launch";
  if (f.station === "ladder") return "ladder";
  if (f.station === "wheel") return "wheel";
  if (f.station.startsWith("cannon:") || f.station.startsWith("islandCannon:")) return "cannon";
  if (f.station.startsWith("capstan:")) return "capstan";
  if (f.carrying) return "chest";
  if (f.swimming) return "swim";
  if (f.work === "repair") return "repair";
  if (f.work === "fill" || f.work === "throw") return f.work;
  if (f.work === "anchorKick") return "anchorKick";
  if (f.recovering) return "recover";
  if (f.digging) return "dig";
  return "idle";
}

const smooth = (t: number): number => { const v = Math.max(0, Math.min(1, t)); return v * v * (3 - 2 * v); };

/** Shovel stroke: wind-up (negative), plunge to 1, then recovery to rest. */
export function digSwing(progress: number): number {
  const p = Math.max(0, Math.min(1, progress));
  return p < 0.25 ? -smooth(p / 0.25) * 0.35 : p < 0.55 ? -0.35 + smooth((p - 0.25) / 0.30) * 1.35 : 1 - smooth((p - 0.55) / 0.45);
}
